import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useEntity } from '../contexts/EntityContext';
import { useTheme } from '../contexts/ThemeContext';
import { LanguageSelector } from './LanguageSelector';
import { ConfirmModal } from './ui/Modal';
import { CashierSidebar } from './cashier/CashierSidebar';
import './Layout.css';

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { currentEntity } = useEntity();
  const { theme, toggleTheme } = useTheme();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isAccountant = user?.role === 'accountant' || isAdmin;
  const isCashier = user?.role === 'cashier';
  const canViewReports = !isCashier || isAccountant;

  // Cashier uses sidebar layout
  if (isCashier) {
    return (
      <div className="layout layout-cashier-sidebar">
        <CashierSidebar
          onLogout={() => setShowLogoutConfirm(true)}
          onToggleTheme={toggleTheme}
          theme={theme}
        />

        <ConfirmModal
          isOpen={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
          onConfirm={logout}
          title={t('nav.logout')}
          message={t('messages.confirmLogout')}
          confirmText={t('nav.logout')}
          confirmVariant="danger"
        />

        <main className="main-content cashier-main-with-sidebar">
          {currentEntity ? (
            <Outlet />
          ) : (
            <div className="no-entity">
              <h2>{t('actions.selectEntity')}</h2>
              <p>{t('messages.selectOption')}</p>
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1 className="logo">{t('app.name')}</h1>
        </div>

        <nav className="nav">
          <NavLink to="/" className="nav-link" end>
            {t('nav.dashboard')}
          </NavLink>
          <NavLink to="/transactions" className="nav-link">
            {t('nav.transactions')}
          </NavLink>
          <NavLink to="/debt" className="nav-link">
            {t('nav.debts')}
          </NavLink>
          <NavLink to="/movements" className="nav-link">
            {t('nav.movements')}
          </NavLink>
          {canViewReports && (
            <NavLink to="/reports" className="nav-link">
              {t('nav.reports')}
            </NavLink>
          )}

          <div className="nav-section">{t('nav.admin')}</div>
          {isAdmin && (
            <>
              <NavLink to="/admin/users" className="nav-link">
                {t('nav.users')}
              </NavLink>
              <NavLink to="/admin/entities" className="nav-link">
                {t('nav.entities')}
              </NavLink>
              <NavLink to="/admin/clients" className="nav-link">
                {t('nav.clients')}
              </NavLink>
            </>
          )}
          <NavLink to="/admin/wallets" className="nav-link">
            {t('nav.wallets')}
          </NavLink>
          {isAccountant && (
            <>
              <NavLink to="/admin/periods" className="nav-link">
                {t('nav.periods')}
              </NavLink>
              <NavLink to="/admin/categories" className="nav-link">
                {t('nav.categories')}
              </NavLink>
              <NavLink to="/admin/daily-balances" className="nav-link">
                {t('nav.dailyBalances')}
              </NavLink>
            </>
          )}
          {isAdmin && (
            <>
              <NavLink to="/admin/currencies" className="nav-link">
                {t('nav.currencies')}
              </NavLink>
              <NavLink to="/admin/cash-desks" className="nav-link">
                Группы касс
              </NavLink>
              <NavLink to="/admin/logs" className="nav-link">
                {t('nav.auditLog')}
              </NavLink>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-name">{user?.fullName}</div>
            <div className="user-role">
              <span className={`badge badge-${user?.role}`}>{user?.role}</span>
            </div>
          </div>
          <div className="footer-controls">
            <LanguageSelector />
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              title={theme === 'light' ? t('theme.dark') : t('theme.light')}
            >
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
          </div>
          <button className="btn btn-secondary" onClick={() => setShowLogoutConfirm(true)}>
            {t('nav.logout')}
          </button>
          <div className="version-info">v{__APP_VERSION__}</div>
        </div>
      </aside>

      <ConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={logout}
        title={t('nav.logout')}
        message={t('messages.confirmLogout')}
        confirmText={t('nav.logout')}
        confirmVariant="danger"
      />

      <main className="main-content">
        {currentEntity ? (
          <Outlet />
        ) : (
          <div className="no-entity">
            <h2>{t('actions.selectEntity')}</h2>
            <p>{t('messages.selectOption')}</p>
          </div>
        )}
      </main>
    </div>
  );
}
